import React, { useState } from 'react';
import { X, Copy, CheckCircle, AlertTriangle, ClipboardList, ChevronDown, ChevronUp, FileDown } from 'lucide-react';
import { generateChargeHistoryDocx } from '../utils/generateChargeHistoryDocx';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirrors the API response shape)
// ─────────────────────────────────────────────────────────────────────────────

interface ConvictionRow {
  date: string;
  charge: string;
  disposition: string;
  sentence?: string;
}

interface ArrestRow {
  date: string;
  charge: string;
  disposition: string;
}

interface FtaRow {
  date: string;
}

export interface ChargeHistoryData {
  defendantName: string;
  aliases: string[];
  currentArrestDate: string;
  convictions: ConvictionRow[];
  arrests: ArrestRow[];
  ftas: FtaRow[];
  reviewNotes: string[];
  rawEntriesProcessed: number;
  duplicatesRemoved: number;
}

interface Props {
  data: ChargeHistoryData;
  evidenceTitle: string;
  loading: boolean;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Review checklist items — BSAO mandatory pre-submission checks
// ─────────────────────────────────────────────────────────────────────────────

const REVIEW_ITEMS = [
  'All conviction dates verified (disposition date used for Guilty/PBJ)',
  'All arrest dates verified (arrest date used for Nol Pros/Stet/Dismissed)',
  'FTA dates confirmed within 3-year window from current arrest',
  'No instant offense (current arrest) included in history',
  'Duplicate TRK# entries confirmed removed',
  'Aliases confirmed (no Master/Arrest name variants included)',
  'VOP entries annotated, not counted as separate arrests',
];

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function ChargeHistoryTable({ data, loading, onClose }: Props) {
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showChecklist, setShowChecklist] = useState(true);

  const allChecked = REVIEW_ITEMS.every((_, i) => checkedItems[i]);

  const toggleCheck = (i: number) => {
    setCheckedItems(prev => ({ ...prev, [i]: !prev[i] }));
  };

  const buildPlainText = (): string => {
    const lines: string[] = [];
    lines.push(`CHARGE HISTORY — ${data.defendantName}`);
    lines.push(`Generated: ${new Date().toLocaleDateString('en-US')}`);
    lines.push('');

    if (data.convictions.length > 0) {
      lines.push('CONVICTIONS:');
      for (const c of data.convictions) {
        const sentence = c.sentence ? ` (${c.sentence})` : '';
        lines.push(`  ${c.date} — ${c.charge} — ${c.disposition}${sentence}`);
      }
      lines.push('');
    } else {
      lines.push('CONVICTIONS: None');
      lines.push('');
    }

    if (data.arrests.length > 0) {
      lines.push('ARRESTS (NO CONVICTION):');
      for (const a of data.arrests) {
        lines.push(`  ${a.date} — ${a.charge} — ${a.disposition}`);
      }
      lines.push('');
    }

    if (data.ftas.length > 0) {
      lines.push(`FTAs WITHIN LAST 3 YEARS:`);
      lines.push('  ' + data.ftas.map(f => f.date).join(', '));
      lines.push('');
    }

    if (data.aliases.length > 0) {
      lines.push(`ALIASES: ${data.aliases.join(', ')}`);
    } else {
      lines.push('ALIASES: None');
    }

    return lines.join('\n');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // fallback
    }
  };

  const handleDownloadDocx = async () => {
    try {
      setGenerating(true);
      await generateChargeHistoryDocx(data);
    } catch (err) {
      console.error('Failed to generate DOCX:', err);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex flex-col">
        <PanelHeader title="Charge History" onClose={onClose} />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
          <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          <div>
            <p className="text-sm font-medium text-gray-800">Extracting Charge History</p>
            <p className="text-xs text-gray-500 mt-1">Stage 1: parsing RAP sheet structure…</p>
            <p className="text-xs text-gray-400 mt-0.5">Stage 2: GPT-4o applying BSAO rules…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <PanelHeader title="Charge History" subtitle={data.defendantName} onClose={onClose} />

      {/* Stats bar */}
      <div className="px-4 py-2 bg-indigo-50 border-b border-indigo-100 flex items-center gap-4 text-xs text-indigo-700">
        <span>{data.rawEntriesProcessed} entries processed</span>
        <span className="w-px h-3 bg-indigo-200" />
        <span>{data.duplicatesRemoved} duplicates removed</span>
        <span className="w-px h-3 bg-indigo-200" />
        <span>FTA window: 3 yrs from {data.currentArrestDate}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* ── Convictions ─────────────────────────────────────────────────── */}
        <Section title={`Convictions (${data.convictions.length})`} accent="emerald" empty={data.convictions.length === 0}>
          {data.convictions.length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-1 pr-2 font-medium w-16">Date</th>
                  <th className="pb-1 pr-2 font-medium">Charge</th>
                  <th className="pb-1 font-medium w-14">Disp.</th>
                </tr>
              </thead>
              <tbody>
                {data.convictions.map((c, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b border-gray-50 last:border-0">
                      <td className="py-1.5 pr-2 font-mono text-gray-700 align-top">{c.date}</td>
                      <td className="py-1.5 pr-2 text-gray-900 align-top">
                        <div className="font-medium">{c.charge}</div>
                        {c.sentence && (
                          <div className="text-gray-500 text-[10px] mt-0.5">{c.sentence}</div>
                        )}
                      </td>
                      <td className="py-1.5 align-top">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800">
                          {c.disposition}
                        </span>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-gray-400 italic">No convictions found</p>
          )}
        </Section>

        {/* ── Arrests (No Conviction) ──────────────────────────────────────── */}
        <Section title={`Arrests, No Conviction (${data.arrests.length})`} accent="gray" empty={data.arrests.length === 0}>
          {data.arrests.length > 0 ? (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="pb-1 pr-2 font-medium w-16">Date</th>
                  <th className="pb-1 pr-2 font-medium">Charge</th>
                  <th className="pb-1 font-medium w-16">Disp.</th>
                </tr>
              </thead>
              <tbody>
                {data.arrests.map((a, i) => (
                  <tr key={i} className="border-b border-gray-50 last:border-0">
                    <td className="py-1.5 pr-2 font-mono text-gray-700 align-top">{a.date}</td>
                    <td className="py-1.5 pr-2 text-gray-900 align-top">{a.charge}</td>
                    <td className="py-1.5 align-top">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700">
                        {a.disposition}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-gray-400 italic">None</p>
          )}
        </Section>

        {/* ── FTAs ─────────────────────────────────────────────────────────── */}
        <Section title={`FTAs within 3 Years (${data.ftas.length})`} accent="amber" empty={data.ftas.length === 0}>
          {data.ftas.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.ftas.map((f, i) => (
                <span key={i} className="inline-flex items-center px-2 py-1 rounded bg-amber-100 text-amber-800 text-xs font-mono">
                  {f.date}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">None within 3-year window</p>
          )}
        </Section>

        {/* ── Aliases ──────────────────────────────────────────────────────── */}
        <Section title="Aliases" accent="blue" empty={false}>
          {data.aliases.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {data.aliases.map((a, i) => (
                <span key={i} className="text-xs bg-blue-50 text-blue-800 px-2 py-1 rounded">{a}</span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">None (all names match Master/Arrest name)</p>
          )}
        </Section>

        {/* ── GPT review notes ─────────────────────────────────────────────── */}
        {data.reviewNotes?.length > 0 && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-1.5 mb-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
              <p className="text-xs font-medium text-amber-800">Notes for Review</p>
            </div>
            {data.reviewNotes.map((n, i) => (
              <p key={i} className="text-xs text-amber-700 mt-0.5">• {n}</p>
            ))}
          </div>
        )}

        {/* ── Review Checklist ─────────────────────────────────────────────── */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <button
            onClick={() => setShowChecklist(s => !s)}
            className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 text-xs font-medium text-gray-700"
          >
            <span className="flex items-center gap-1.5">
              <ClipboardList className="h-3.5 w-3.5" />
              Human Review Checklist
              {allChecked && <CheckCircle className="h-3.5 w-3.5 text-emerald-600 ml-1" />}
            </span>
            {showChecklist ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
          {showChecklist && (
            <div className="px-3 py-2 space-y-2">
              {REVIEW_ITEMS.map((item, i) => (
                <label key={i} className="flex items-start gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={!!checkedItems[i]}
                    onChange={() => toggleCheck(i)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                  />
                  <span className={`text-xs leading-relaxed ${checkedItems[i] ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {item}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>

      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 space-y-2">
        <button
          onClick={handleCopy}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
        >
          {copied
            ? <><CheckCircle className="h-4 w-4" /> Copied to Clipboard</>
            : <><Copy className="h-4 w-4" /> Copy as BSAO Summary</>
          }
        </button>
        <button
          onClick={handleDownloadDocx}
          disabled={generating}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {generating
            ? <><div className="h-4 w-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" /> Generating...</>
            : <><FileDown className="h-4 w-4" /> Generate Summary</>
          }
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function PanelHeader({ title, subtitle, onClose }: { title: string; subtitle?: string; onClose: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
      <div>
        <div className="flex items-center gap-2">
          <ClipboardList className="h-4 w-4 text-indigo-600" />
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        </div>
        {subtitle && <p className="text-xs text-gray-500 mt-0.5 ml-6">{subtitle}</p>}
      </div>
      <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

const ACCENT_COLORS = {
  emerald: { header: 'bg-emerald-50 text-emerald-800 border-emerald-200', border: 'border-emerald-200' },
  gray:    { header: 'bg-gray-50 text-gray-800 border-gray-200',          border: 'border-gray-200' },
  amber:   { header: 'bg-amber-50 text-amber-800 border-amber-200',       border: 'border-amber-200' },
  blue:    { header: 'bg-blue-50 text-blue-800 border-blue-200',          border: 'border-blue-200' },
};

function Section({
  title, accent, children
}: {
  title: string;
  accent: keyof typeof ACCENT_COLORS;
  children: React.ReactNode;
  empty?: boolean;
}) {
  const colors = ACCENT_COLORS[accent];
  return (
    <div className={`border rounded-lg overflow-hidden ${colors.border}`}>
      <div className={`px-3 py-2 text-xs font-semibold ${colors.header}`}>
        {title}
      </div>
      <div className="px-3 py-2">
        {children}
      </div>
    </div>
  );
}
